import React, { useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Sparkle, ArrowsClockwise, WarningCircle, Info } from '@phosphor-icons/react';
import { toast } from 'sonner';
import useIntelligenceState from '@/hooks/useIntelligenceState';

/**
 * LiveIntelligenceCard — Round 218A Phase 8D-3C (2026-02-06).
 *
 * Dashboard card that surfaces the fallback → live upgrade progress
 * for Market Intelligence and Opportunity Intelligence. Reads the
 * ``useIntelligenceState`` hook (polling GET /api/onboarding/
 * intelligence/state). Auto-refreshes card contents when the
 * scheduler flips a type from ``ready_fallback`` /
 * ``live_upgrade_queued`` / ``live_upgrade_running`` to
 * ``live_ready`` — no full page reload required.
 *
 * Honest data-source chips: only shows ``Tavily`` / ``Gemini`` when
 * the backend row's ``data_sources`` actually contains them.
 */
const STATE_LABEL = {
  ready_fallback: 'Starter plan (upgrading in the background)',
  live_upgrade_queued: 'Researching now…',
  live_upgrade_running: 'Researching now…',
  live_ready: 'Live',
  live_upgrade_failed_retry_pending: 'Retrying research…',
  not_started: 'Preparing…',
};

const STATE_COLOR = {
  ready_fallback: 'from-slate-500/20 to-slate-600/10 border-slate-500/30',
  live_upgrade_queued: 'from-cyan-500/20 to-blue-500/10 border-cyan-500/30',
  live_upgrade_running: 'from-cyan-500/20 to-blue-500/10 border-cyan-500/30',
  live_ready: 'from-emerald-500/20 to-cyan-500/10 border-emerald-500/40',
  live_upgrade_failed_retry_pending: 'from-amber-500/20 to-orange-500/10 border-amber-500/30',
  not_started: 'from-slate-500/10 to-slate-600/5 border-slate-500/20',
};

const DATA_SOURCE_LABEL = {
  tavily: 'Tavily',
  gemini: 'Gemini',
  business_brain: 'Business Brain',
  brand_brain: 'Brand Brain',
  business_profile: 'Profile',
  market_intelligence: 'MI',
  onboarding_answers: 'Onboarding',
  catalog: 'Catalog',
};

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch (_e) {
    return iso;
  }
}

const Chip = ({ children, tone = 'slate' }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium
    ${tone === 'emerald' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
      : tone === 'cyan' ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
      : tone === 'amber' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
      : 'bg-slate-500/20 text-slate-300 border border-slate-500/30'}`}>
    {children}
  </span>
);

const IntelPanel = ({ title, intel, testidPrefix }) => {
  const state = intel?.state || 'not_started';
  const row = intel?.row || {};
  const sources = intel?.data_sources || [];
  const tone = state === 'live_ready' ? 'emerald' :
    (state === 'live_upgrade_running' || state === 'live_upgrade_queued') ? 'cyan' :
    state === 'live_upgrade_failed_retry_pending' ? 'amber' : 'slate';
  const StateIcon = state === 'live_ready' ? CheckCircle :
    state === 'live_upgrade_failed_retry_pending' ? WarningCircle :
    ArrowsClockwise;
  const label = STATE_LABEL[state] || 'Preparing…';
  const isSpinning = state === 'live_upgrade_running' || state === 'live_upgrade_queued' || state === 'live_upgrade_failed_retry_pending';

  return (
    <div
      className={`rounded-lg p-3 bg-gradient-to-br ${STATE_COLOR[state] || STATE_COLOR.ready_fallback}`}
      data-testid={`${testidPrefix}-panel`}
      data-state={state}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <h4 className="text-white text-xs font-semibold">{title}</h4>
          {typeof intel?.version === 'number' && (
            <span className="text-slate-400 text-[10px]" data-testid={`${testidPrefix}-version`}>v{intel.version}</span>
          )}
        </div>
        <Chip tone={tone}>
          <StateIcon size={10} weight="bold" className={isSpinning ? 'animate-spin' : ''} />
          <span data-testid={`${testidPrefix}-state`}>{label}</span>
        </Chip>
      </div>

      {/* Primary body — differs by intel type. */}
      {title === 'Market Intelligence' ? (
        <div className="space-y-1">
          {row.audience && (
            <p className="text-slate-200 text-xs" data-testid={`${testidPrefix}-audience`}>
              <span className="text-slate-400 text-[10px] uppercase tracking-wider block">Audience</span>
              {row.audience}
            </p>
          )}
          {Array.isArray(row.channels) && row.channels.length > 0 && (
            <p className="text-slate-300 text-[11px] mt-1">
              <span className="text-slate-400 text-[10px] uppercase tracking-wider block">Channels</span>
              {row.channels.slice(0, 3).join(' · ')}
            </p>
          )}
          {row.highest_roi_recommendation && (
            <p className="text-emerald-200 text-[11px] mt-2 leading-snug" data-testid={`${testidPrefix}-hi-roi`}>
              <Sparkle size={10} weight="fill" className="inline mr-1 text-emerald-400" />
              {typeof row.highest_roi_recommendation === 'string'
                ? row.highest_roi_recommendation
                : (row.highest_roi_recommendation?.action || '')}
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-1">
          {row.opportunity && (
            <p className="text-slate-200 text-xs" data-testid={`${testidPrefix}-opportunity`}>
              <span className="text-slate-400 text-[10px] uppercase tracking-wider block">Opportunity</span>
              {row.opportunity}
            </p>
          )}
          {row.next_step && (
            <p className="text-emerald-200 text-[11px] mt-2 leading-snug" data-testid={`${testidPrefix}-next-step`}>
              <Sparkle size={10} weight="fill" className="inline mr-1 text-emerald-400" />
              Next: {row.next_step}
            </p>
          )}
        </div>
      )}

      {/* Data sources — HONEST. Only shows what the backend actually stamped. */}
      {sources.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2" data-testid={`${testidPrefix}-sources`}>
          {sources.map((s) => (
            <span
              key={s}
              className={`text-[9px] px-1.5 py-0.5 rounded ${
                s === 'tavily' || s === 'gemini'
                  ? 'bg-emerald-500/20 text-emerald-300'
                  : 'bg-slate-500/20 text-slate-400'
              }`}
              data-testid={`${testidPrefix}-source-${s}`}
            >
              {DATA_SOURCE_LABEL[s] || s}
            </span>
          ))}
        </div>
      )}

      {intel?.generated_at && (
        <p className="text-slate-500 text-[9px] mt-2">
          Updated {formatDate(intel.generated_at)}
        </p>
      )}
    </div>
  );
};

const LiveIntelligenceCard = ({ onLiveReady }) => {
  const [dismissed, setDismissed] = useState(false);

  const handleLiveReady = useCallback((intelType, intel) => {
    const label = intelType === 'market_intelligence' ? 'Market Intelligence' : 'Opportunity Intelligence';
    toast.success(`${label} live — powered by your latest research.`, {
      description: intel?.data_sources?.includes('tavily')
        ? 'Live web research + AI synthesis complete.'
        : 'AI synthesis complete.',
    });
    if (typeof onLiveReady === 'function') {
      try { onLiveReady(intelType, intel); } catch (_e) { /* ignore */ }
    }
  }, [onLiveReady]);

  const { marketIntel, opportunityIntel, loading, error } = useIntelligenceState({
    onLiveReady: handleLiveReady,
  });

  if (dismissed) return null;

  const mState = marketIntel?.state;
  const oState = opportunityIntel?.state;
  const bothLive = mState === 'live_ready' && oState === 'live_ready';

  if (loading && !marketIntel && !opportunityIntel) {
    return (
      <div
        className="mb-4 p-4 bg-gradient-to-br from-cyan-500/10 to-emerald-500/10 border border-cyan-500/30 rounded-xl"
        data-testid="live-intelligence-card-loading"
      >
        <p className="text-slate-300 text-xs">Loading your live intelligence…</p>
      </div>
    );
  }

  if (error && !marketIntel && !opportunityIntel) {
    return null; // silent — the classic MarketIntelligenceCard covers the visible surface
  }

  if (!marketIntel && !opportunityIntel) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        className="mb-4 p-4 bg-gradient-to-br from-cyan-500/10 to-emerald-500/10 border border-cyan-500/30 rounded-xl"
        data-testid="live-intelligence-card"
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2">
              <Sparkle size={16} weight="fill" className="text-cyan-300" />
              <h3 className="text-white text-sm font-semibold" data-testid="live-intel-title">
                {bothLive ? 'Your live strategy' : 'Your strategy is upgrading'}
              </h3>
            </div>
            <p className="text-slate-400 text-[11px] mt-0.5">
              {bothLive
                ? 'Grounded in your Business Brain + live web research.'
                : 'Starter plan is live now. Live research + AI synthesis are running in the background.'}
            </p>
          </div>
          {bothLive && (
            <button
              onClick={() => setDismissed(true)}
              className="text-slate-500 hover:text-slate-300 text-[10px]"
              data-testid="live-intel-dismiss"
              type="button"
            >
              Dismiss
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <IntelPanel
            title="Market Intelligence"
            intel={marketIntel}
            testidPrefix="live-intel-mi"
          />
          <IntelPanel
            title="Opportunity Intelligence"
            intel={opportunityIntel}
            testidPrefix="live-intel-oi"
          />
        </div>

        {(mState === 'live_upgrade_failed_retry_pending' || oState === 'live_upgrade_failed_retry_pending') && (
          <div className="mt-2 flex items-start gap-1.5 text-amber-300/90 text-[10px]" data-testid="live-intel-retry-hint">
            <Info size={12} weight="bold" className="mt-0.5" />
            <span>
              Live research had a hiccup. Your Starter plan is still active, and we&apos;re retrying automatically.
            </span>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default LiveIntelligenceCard;
